import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes, Global, css } from '@emotion/react';
import * as d3 from 'd3';
import { trackEvent, trackClick } from '../utils/analytics';

const scanline = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 0.4;
  }
  100% {
    transform: translateY(100%);
    opacity: 0.2;
  }
`;

const dotPattern = keyframes`
  0% {
    background-position: 0 0;
    opacity: 0.5;
  }
  100% {
    background-position: 100px 100px;
    opacity: 0.3;
  }
`;

const matrixBg = keyframes`
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-20px);
  }
`;

const glitch = keyframes`
  0% {
    transform: translate(0);
    opacity: 1;
  }
  1% {
    transform: translate(-2px, 2px);
    opacity: 0.8;
  }
  2% {
    transform: translate(2px, -2px);
    opacity: 0.9;
  }
  3% {
    transform: translate(0);
    opacity: 1;
  }
  7% {
    transform: translate(0);
    opacity: 1;
  }
  8% {
    transform: translate(-4px, 0);
    opacity: 0.7;
  }
  9% {
    transform: translate(4px, 0);
    opacity: 0.9;
  }
  10% {
    transform: translate(0);
    opacity: 1;
  }
  100% {
    transform: translate(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(255, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 0, 0, 0.5);
  }
  100% {
    box-shadow: 0 0 10px rgba(255, 0, 0, 0.3);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
`;

const GlobalStyles = () => (
  <Global
    styles={css`
      * {
        scrollbar-width: thin;
        scrollbar-color: rgba(255, 0, 0, 0.3) #0a0a0a;
      }

      *::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }

      *::-webkit-scrollbar-track {
        background: #0a0a0a;
        border: 1px solid rgba(255, 0, 0, 0.1);
        border-radius: 4px;
      }

      *::-webkit-scrollbar-thumb {
        background: rgba(255, 0, 0, 0.3);
        border-radius: 4px;
        border: 1px solid rgba(255, 0, 0, 0.2);
        
        &:hover {
          background: rgba(255, 0, 0, 0.5);
          border-color: rgba(255, 0, 0, 0.4);
        }
      }

      body {
        overflow-y: scroll;
        margin: 0;
        padding: 0;
      }
    `}
  />
);

const Container = styled.div`
  min-height: 100vh;
  height: 100vh;
  background: #0a0a0a;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Courier New', monospace;
  position: relative;
  padding: 0;
  overflow: hidden; // Added to prevent outer scrollbar

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: 
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.05) 2px, transparent 2px);
    background-size: 15px 15px, 30px 30px;
    animation: ${matrixBg} 20s linear infinite;
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: '';
    position: fixed;
    inset: 0;
    background: 
      linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
      linear-gradient(0deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
    background-size: 20px 20px;
    animation: ${dotPattern} 15s linear infinite;
    pointer-events: none;
    z-index: 2;
  }
`;

const BackgroundEffects = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 3;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      0deg,
      rgba(0, 255, 0, 0.03) 0px,
      rgba(0, 255, 0, 0.03) 1px,
      transparent 2px,
      transparent 4px
    );
    animation: ${scanline} 8s linear infinite;
  }

  &::after {
    content: '';
  position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 50% 50%,
      rgba(0, 255, 0, 0.1) 0%,
      transparent 60%
    );
    opacity: 0.5;
  }
`;

const MainDocument = styled.div`
  width: 100%;
  max-width: 1200px;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2rem;
  position: relative;
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
  z-index: 2;
  margin: 2rem auto;
  height: calc(100vh - 4rem);
  overflow-y: auto;
  overflow-x: hidden; // Hide horizontal scrollbar

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0, 0, 0, 0.1) 2px,
      rgba(0, 0, 0, 0.1) 4px
    );
    pointer-events: none;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 2.5rem;
  margin: 0;
  font-family: 'Noto Sans Mongolian', serif;
  letter-spacing: 2px;
  position: relative;
  text-transform: uppercase;
`;

const Subtitle = styled.div`
  color: #666;
  font-size: 1rem;
  margin-top: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 3px;
`;

const ClassifiedStamp = styled.div`
  position: absolute;
  top: -25px;
  right: -25px;
  background: #000;
  color: #fff;
  padding: 5px 15px;
  transform: rotate(15deg);
  font-size: 1rem;
  font-weight: bold;
  letter-spacing: 2px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  border: 2px solid #fff;
  animation: ${pulse} 2s infinite;
  z-index: 10;
`;

const LoginSection = styled.div`
  border: 2px solid #333;
  padding: 2rem;
  background: #0a0a0a;
  position: relative;
  margin: 2rem auto;
  max-width: 500px;
  transition: all 0.3s ease;

  &:hover {
    border-color: #666;
    box-shadow: 0 0 20px rgba(51, 51, 51, 0.3);
  }

  &::before {
    content: 'TOP SECRET';
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: #0a0a0a;
    padding: 0 10px;
    color: #666;
    font-size: 0.8rem;
    letter-spacing: 2px;
  }
`;

const Input = styled.input`
  width: 100%;
  background: #000;
  border: 1px solid #333;
  color: #ff0000;
  padding: 1rem;
  font-family: 'Courier New', monospace;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  outline: none;
  transition: all 0.3s ease;
  text-align: center;
  letter-spacing: 2px;

  &:focus {
    border-color: #ff0000;
    box-shadow: 0 0 10px rgba(255, 0, 0, 0.2);
  }

  &::placeholder {
    color: #ff0000;
    opacity: 0.5;
  }
`;

const Button = styled.button`
  width: 100%;
  background: #1a1a1a;
  border: 1px solid #333;
  color: #fff;
  padding: 1rem;
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 2px;
  position: relative;
  overflow: hidden;

  &:hover {
    background: #333;
    border-color: #666;
  }

  &:active {
    transform: translateY(1px);
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transform: translateX(-100%);
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: translateX(100%);
  }
`;

const ErrorMessage = styled.div`
  color: #ff0000;
  text-align: center;
  margin-top: 1rem;
  font-size: 0.9rem;
  animation: ${glitch} 0.3s infinite;
`;

const AudioPlayer = styled.audio`
  display: none;
`;

const HitListContent = styled.div`
  padding: 2rem;
  background: #111;
  border: 1px solid #333;
  margin-top: 2rem;
`;

const TargetProfile = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
`;

const ProfileImage = styled.div`
  width: 300px;
  height: 300px;
  background-image: url('/images/subject-7749.jpg');
  background-size: cover;
  background-position: center;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.2);
  filter: grayscale(100%) contrast(120%) brightness(90%);
  
  &::before {
    content: '';
    position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
    background: linear-gradient(
      rgba(0, 0, 0, 0.2),
      rgba(0, 0, 0, 0.1)
    );
    animation: ${glitch} 4s infinite;
  }

  &::after {
    content: '121505-12222';
    position: absolute;
    bottom: 10px;
    left: 10px;
    font-family: 'Share Tech Mono', monospace;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.8);
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
  }
`;

const ProfileInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  border-bottom: 1px dotted #333;
  padding: 0.25rem 0;
  font-size: 0.9rem;
`;

const InfoLabel = styled.div`
  width: 120px;
  color: #666;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 1px;
`;

const InfoValue = styled.div`
  color: #fff;
  flex: 1;
  font-family: 'Courier New', monospace;
`;

const RedactedText = styled.span`
  background: #000;
  color: #000;
  padding: 0 5px;
  margin: 0 5px;
  position: relative;
  user-select: none;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    background: #333;
  }
`;

const PsychEvaluation = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
  font-family: 'Courier New', monospace;
  font-size: 0.85rem;
  line-height: 1.4;
`;

const EvalHeader = styled.div`
  color: #666;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
  font-size: 0.8rem;
  border-bottom: 1px solid rgba(255, 0, 0, 0.2);
  padding-bottom: 0.25rem;
`;

const EvalContent = styled.div`
  color: #fff;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
`;

const EvalSection = styled.div`
  margin-bottom: 0.5rem;
`;

const WarningText = styled.div`
  color: #ff0000;
  text-transform: uppercase;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  padding: 0.25rem;
  border: 1px solid rgba(255, 0, 0, 0.3);
  background: rgba(255, 0, 0, 0.1);
  text-align: center;
`;

interface Document {
  title: string;
  content: string;
  category: 'ALIEN' | 'CYBER' | 'PARANORMAL' | 'CONSPIRACY' | 'WAR_CRIME' | 'HUMAN_EXPERIMENTS' | 'SURVEILLANCE';
  date?: string;
  classification?: 'TOP SECRET' | 'SECRET' | 'CONFIDENTIAL';
  redactedText?: boolean;
  location?: string;
  operationStatus?: string;
  projectCode?: string;
  securityLevel?: number;
  involvedPersonnel?: string[];
  relatedFiles?: string[];
  images?: string[];
  warnings?: string[];
  lastModified?: string;
  nextReviewDate?: string;
}

interface ShadowFigure {
  id: string;
  name: string;
  codename: string;
  nationality: string;
  status: string;
  position: string;
  influence: string[];
  operations: string[];
  lastKnownLocation: string;
  associates: string[];
  threatLevel: string;
  imageUrl: string;
  notes: string;
}

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
  backdrop-filter: blur(10px);
  animation: fadeIn 0.3s ease-out;

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: 
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.05) 2px, transparent 2px);
    background-size: 15px 15px, 30px 30px;
    animation: ${matrixBg} 20s linear infinite;
    pointer-events: none;
    z-index: 1;
  }

  &::after {
    content: '';
    position: fixed;
    inset: 0;
    background: 
      linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
      linear-gradient(0deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
    background-size: 20px 20px;
    animation: ${dotPattern} 15s linear infinite;
    pointer-events: none;
    z-index: 2;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: rgba(10, 10, 10, 0.95);
  border: 1px solid #333;
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  padding: 2rem;
  color: #fff;
  font-family: 'Share Tech Mono', monospace;
  z-index: 3;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.03) 1px, transparent 1px),
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.05) 2px, transparent 2px);
    background-size: 15px 15px, 30px 30px;
    animation: ${matrixBg} 20s linear infinite;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px),
      linear-gradient(0deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
    background-size: 20px 20px;
    animation: ${dotPattern} 15s linear infinite;
    pointer-events: none;
    opacity: 0.5;
  }

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #0a0a0a;
    border: 1px solid rgba(255, 0, 0, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 0, 0, 0.3);
    border-radius: 4px;
    border: 1px solid rgba(255, 0, 0, 0.2);
    
    &:hover {
      background: rgba(255, 0, 0, 0.5);
      border-color: rgba(255, 0, 0, 0.4);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 12;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    color: #ff0000;
    transform: scale(1.1);
  }
`;

const ModalClassification = styled.div`
  position: absolute;
  top: 0;
  right: 3rem;
  transform: translateY(-50%);
  background: #000;
  color: #fff;
  padding: 0.5rem 1rem;
  border: 1px solid #ff0000;
  font-size: 0.9rem;
  letter-spacing: 2px;
  z-index: 11;
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-top: 2rem;
  padding-right: 1rem;
  overflow-x: hidden; // Hide horizontal scrollbar

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #0a0a0a;
    border: 1px solid rgba(255, 0, 0, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 0, 0, 0.3);
    border-radius: 4px;
    border: 1px solid rgba(255, 0, 0, 0.2);
    
    &:hover {
      background: rgba(255, 0, 0, 0.5);
      border-color: rgba(255, 0, 0, 0.4);
    }
  }
`;

const DocumentCard = styled.div`
  background: #0a0a0a;
  border: 1px solid #333;
  padding: 1.5rem;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
  margin-top: 1rem;
  padding-top: 2.5rem; // Added padding to make room for classification label

  &:hover {
    border-color: #666;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
    transform: translateY(-2px);
  }
`;

const DocumentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #333;
`;

const DocumentTitle = styled.h3`
  color: #fff;
  font-size: 1.1rem;
  margin: 0;
  font-family: 'Courier New', monospace;
`;

const ClassificationLabel = styled.span`
  position: absolute;
  top: 0;
  right: 1rem;
  transform: translateY(-50%);
  background: #000;
  color: #fff;
  padding: 0.2rem 0.5rem;
  font-size: 0.8rem;
  letter-spacing: 1px;
  border: 1px solid #333;
  z-index: 2;
  white-space: nowrap;
`;

const DocumentContent = styled.div`
  line-height: 1.6;
  white-space: pre-wrap;
  margin-bottom: 2rem;
  font-size: 1rem;
  color: #ccc;
  
  p {
    margin-bottom: 1rem;
  }
`;

const CategoryBadge = styled.span<{ $category: string }>`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
  background: ${props => {
    switch (props.$category) {
      case 'WAR_CRIME': return 'rgba(255, 255, 255, 0.1)';
      case 'HUMAN_EXPERIMENTS': return 'rgba(255, 255, 255, 0.1)';
      case 'SURVEILLANCE': return 'rgba(255, 255, 255, 0.1)';
      case 'CONSPIRACY': return 'rgba(255, 255, 255, 0.1)';
      case 'ALIEN': return 'rgba(255, 255, 255, 0.1)';
      case 'CYBER': return 'rgba(255, 255, 255, 0.1)';
      case 'PARANORMAL': return 'rgba(255, 255, 255, 0.1)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const Tab = styled.button<{ $active: boolean }>`
  background: ${props => props.$active ? '#333' : 'transparent'};
  border: 1px solid ${props => props.$active ? '#666' : '#333'};
  color: #fff;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;

  &:hover {
    border-color: #666;
  }
`;

const FinalMessage = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  border-top: 1px solid #333;
  color: #ff0000;
  text-align: center;
  font-family: 'Courier New', monospace;
  font-size: 1.5rem;
  font-weight: bold;
  letter-spacing: 2px;
  animation: ${glitch} 2s infinite;
  text-shadow: 0 0 10px rgba(255, 0, 0, 0.5);
`;

const DocumentImages = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin: 2rem 0;
`;

const DocumentImage = styled.div`
  position: relative;
  aspect-ratio: 16/9;
  background: #111;
  overflow: hidden;
  border: 1px solid #333;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const WarningBanner = styled.div`
  background: rgba(255, 0, 0, 0.1);
  border: 1px solid #ff0000;
  padding: 1rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  color: #ff0000;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const RelatedFiles = styled.div`
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #333;
`;

const RelatedFile = styled.div`
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ModalHeader = styled.div`
  border-bottom: 1px solid #333;
  padding-bottom: 1rem;
  margin-bottom: 2rem;
  position: relative;
  padding-top: 2rem; // Added padding to make room for classification label
`;

const ModalTitle = styled.h2`
  font-size: 2rem;
  margin: 0;
  font-family: 'Share Tech Mono', monospace;
  letter-spacing: 1px;
`;

const ModalMetadata = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #333;
`;

const MetadataItem = styled.div`
  font-size: 0.9rem;
  
  span {
    color: #666;
    margin-right: 0.5rem;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-top: 2rem;
`;

const ProfileCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
  padding: 1.5rem;
  display: flex;
  gap: 2rem;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 0, 0, 0.4);
    transform: translateY(-5px);
  }

  &::before {
    content: '';
  position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 10px,
      rgba(0, 0, 0, 0.1) 10px,
      rgba(0, 0, 0, 0.1) 20px
    );
    pointer-events: none;
  }
`;

const ProfilePhoto = styled.div<{ $url: string }>`
  width: 200px;
  height: 250px;
  background-image: url(${props => props.$url});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #111; // Fallback color
  border: 1px solid rgba(255, 255, 255, 0.2);
  filter: grayscale(100%) contrast(120%) brightness(90%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      rgba(0, 0, 0, 0.2),
      rgba(0, 0, 0, 0.1)
    );
    animation: ${glitch} 4s infinite;
  }

  &::after {
    content: attr(data-id);
    position: absolute;
    bottom: 10px;
    left: 10px;
    font-family: 'Share Tech Mono', monospace;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.8);
    text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
  }

  // Add error handling for missing images
  &.image-error {
    &::before {
      content: 'IMAGE CLASSIFIED';
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: rgba(255, 0, 0, 0.7);
      font-family: 'Share Tech Mono', monospace;
      font-size: 1rem;
      padding: 1rem;
      background: rgba(0, 0, 0, 0.8);
    }
  }
`;

const ProfilePhotoContainer: React.FC<{ url: string; id: string }> = ({ url, id }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = url;
    img.onerror = () => setHasError(true);
    img.onload = () => setHasError(false);
  }, [url]);

  return (
    <ProfilePhoto 
      $url={url} 
      data-id={id} 
      className={hasError ? 'image-error' : ''}
    />
  );
};

const ProfileDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ProfileName = styled.div`
  font-size: 1.2rem;
  color: #fff;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: 'Share Tech Mono', monospace;
`;

const ProfileData = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.9rem;
  border-bottom: 1px dotted rgba(255, 255, 255, 0.1);
  padding: 0.25rem 0;

  &:last-child {
    border-bottom: none;
  }
`;

const DataLabel = styled.div`
  color: #666;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 1px;
`;

const DataValue = styled.div`
  color: #fff;
  font-family: 'Courier New', monospace;
`;

const NetworkView = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
`;

const NetworkMap = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
  padding: 1rem;
  height: 400px;
`;

const ConnectionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ConnectionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
`;

const ConnectionType = styled.span`
  color: #666;
  font-size: 0.8rem;
`;

const TimelineView = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TimelineEvent = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
`;

const TimelineDate = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const TimelineContent = styled.div`
  color: #fff;
`;

const AssetsView = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const AssetCategory = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
  padding: 1rem;
`;

const AssetTitle = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 0, 0, 0.2);
  padding-bottom: 0.5rem;
`;

const AssetList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const AssetItem = styled.div`
  color: #fff;
  padding: 0.5rem;
  background: rgba(0, 0, 0, 0.3);
`;

const SurveillanceView = styled.div`
  margin-top: 2rem;
`;

const SurveillanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
`;

const SurveillanceItem = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SurveillanceDate = styled.div`
  color: #666;
  font-size: 0.8rem;
`;

const SurveillanceLocation = styled.div`
  color: #fff;
`;

const OperationsView = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const OperationCard = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
  padding: 1.5rem;
  position: relative;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 0, 0, 0.4);
    transform: translateY(-5px);
  }
`;

const OperationTitle = styled.div`
  font-size: 1.2rem;
  color: #fff;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: 'Share Tech Mono', monospace;
`;

const OperationDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const OperationDetail = styled.div<{ $type: string }>`
  font-family: 'Share Tech Mono', monospace;
  color: #8a8a8a;
  text-shadow: 0 0 5px rgba(138, 138, 138, 0.3);
  padding: 0.25rem 0.5rem;
  background: rgba(0, 0, 0, 0.2);
  border-left: 2px solid rgba(138, 138, 138, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.3);
    border-left-color: rgba(138, 138, 138, 0.5);
  }
`;

const OperationStatus = styled.div<{ $status: string }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
  font-family: 'Share Tech Mono', monospace;
  background: ${props => {
    switch (props.$status) {
      case 'ACTIVE': return 'rgba(0, 255, 0, 0.15)';      // Keep green for active
      case 'TERMINATED': return 'rgba(255, 0, 0, 0.15)';   // Keep red for terminated
      case 'SUSPENDED': return 'rgba(255, 255, 0, 0.15)';  // Keep yellow for suspended
      case 'COMPLETED': return 'rgba(0, 180, 0, 0.15)';    // Darker green for completed
      case 'COMPROMISED': return 'rgba(255, 60, 0, 0.15)'; // Deep orange-red for compromised
      case 'CRITICAL': return 'rgba(255, 0, 60, 0.15)';    // Deep red for critical
      case '[REDACTED]': return 'rgba(150, 0, 0, 0.15)';   // Dark red for redacted
      default: return 'rgba(128, 128, 128, 0.15)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$status) {
      case 'ACTIVE': return 'rgba(0, 255, 0, 0.5)';
      case 'TERMINATED': return 'rgba(255, 0, 0, 0.5)';
      case 'SUSPENDED': return 'rgba(255, 255, 0, 0.5)';
      case 'COMPLETED': return 'rgba(0, 180, 0, 0.5)';
      case 'COMPROMISED': return 'rgba(255, 60, 0, 0.5)';
      case 'CRITICAL': return 'rgba(255, 0, 60, 0.5)';
      case '[REDACTED]': return 'rgba(150, 0, 0, 0.5)';
      default: return 'rgba(128, 128, 128, 0.5)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'ACTIVE': return '#00ff00';
      case 'TERMINATED': return '#ff0000';
      case 'SUSPENDED': return '#ffff00';
      case 'COMPLETED': return '#00b400';
      case 'COMPROMISED': return '#ff3c00';
      case 'CRITICAL': return '#ff003c';
      case '[REDACTED]': return '#960000';
      default: return '#808080';
    }
  }};
  text-shadow: 0 0 5px ${props => {
    switch (props.$status) {
      case 'ACTIVE': return 'rgba(0, 255, 0, 0.5)';
      case 'TERMINATED': return 'rgba(255, 0, 0, 0.5)';
      case 'SUSPENDED': return 'rgba(255, 255, 0, 0.5)';
      case 'COMPLETED': return 'rgba(0, 180, 0, 0.5)';
      case 'COMPROMISED': return 'rgba(255, 60, 0, 0.5)';
      case 'CRITICAL': return 'rgba(255, 0, 60, 0.5)';
      case '[REDACTED]': return 'rgba(150, 0, 0, 0.5)';
      default: return 'rgba(128, 128, 128, 0.5)';
    }
  }};
  backdrop-filter: blur(2px);
  letter-spacing: 1px;
  box-shadow: 0 0 10px ${props => {
    switch (props.$status) {
      case 'ACTIVE': return 'rgba(0, 255, 0, 0.2)';
      case 'TERMINATED': return 'rgba(255, 0, 0, 0.2)';
      case 'SUSPENDED': return 'rgba(255, 255, 0, 0.2)';
      case 'COMPLETED': return 'rgba(0, 180, 0, 0.2)';
      case 'COMPROMISED': return 'rgba(255, 60, 0, 0.2)';
      case 'CRITICAL': return 'rgba(255, 0, 60, 0.2)';
      case '[REDACTED]': return 'rgba(150, 0, 0, 0.2)';
      default: return 'rgba(128, 128, 128, 0.2)';
    }
  }};
`;

const NetworkContainer = styled.div`
  margin-top: 2rem;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  height: calc(100vh - 300px);
`;

const NetworkVisualization = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
  padding: 1.5rem;
  position: relative;
  height: 100%;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(circle at 50% 50%, rgba(255, 0, 0, 0.1) 0%, transparent 70%),
      repeating-linear-gradient(
        0deg,
        transparent,
        transparent 2px,
        rgba(255, 0, 0, 0.05) 2px,
        rgba(255, 0, 0, 0.05) 4px
      );
    pointer-events: none;
  }
`;

const NetworkStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const NetworkStat = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
  padding: 1rem;
`;

const StatLabel = styled.div`
  color: #666;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  color: #fff;
  font-size: 1.2rem;
  font-family: 'Share Tech Mono', monospace;
`;

const TimelineContainer = styled.div`
  margin-top: 2rem;
  padding: 2rem;
  position: relative;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(255, 0, 0, 0.2);
  transform: translateX(-50%);
`;

const TimelineItem = styled.div<{ $align: 'left' | 'right' }>`
  display: flex;
  justify-content: ${props => props.$align === 'left' ? 'flex-end' : 'flex-start'};
  padding: 2rem 0;
  width: 50%;
  margin-left: ${props => props.$align === 'left' ? '0' : '50%'};
  position: relative;

  &::before {
    content: '';
  position: absolute;
  width: 20px;
  height: 20px;
    background: #000;
    border: 2px solid rgba(255, 0, 0, 0.3);
    border-radius: 50%;
    top: 50%;
    ${props => props.$align === 'left' ? 'right: -10px' : 'left: -10px'};
    transform: translateY(-50%);
  }
`;

const TimelineViewDate = styled.div`
  color: #666;
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
`;

const TimelineViewContent = styled.div<{ $align: 'left' | 'right' }>`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 0, 0, 0.2);
  padding: 1.5rem;
  width: 80%;
  margin: ${props => props.$align === 'left' ? '0 2rem 0 0' : '0 0 0 2rem'};
`;

const TimelineTitle = styled.div`
  color: #fff;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-family: 'Share Tech Mono', monospace;
`;

const TimelineDescription = styled.div`
  color: #ccc;
  font-size: 0.9rem;
`;

interface NetworkNode extends d3.SimulationNodeDatum {
  id: string;
  name: string;
  threatLevel: string;
  radius: number;
  x?: number;
  y?: number;
}

interface NetworkLink extends d3.SimulationLinkDatum<NetworkNode> {
  source: NetworkNode;
  target: NetworkNode;
  value: number;
}

const NetworkVisualizationComponent: React.FC<{ 
  data: ShadowFigure[],
  selectedProfileId?: string,
  selectedProfileAssociates?: string[]
}> = ({ data, selectedProfileId, selectedProfileAssociates }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    d3.select(svgRef.current).selectAll("*").remove();

    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;

    // Filter nodes if we have a selected profile
    const filteredData = selectedProfileId && selectedProfileAssociates
      ? data.filter(figure => 
          figure.id === selectedProfileId || 
          selectedProfileAssociates.includes(figure.name)
        )
      : data;

    // Enhanced nodes with more properties
    const nodes: NetworkNode[] = filteredData.map(figure => ({
      id: figure.id,
      name: figure.name,
      threatLevel: figure.threatLevel,
      radius: figure.threatLevel === "MAXIMUM" ? 25 : 
              figure.threatLevel === "SEVERE" ? 20 : 
              figure.threatLevel === "HIGH" ? 15 : 12,
      group: figure.nationality
    }));

    // Create more complex links with multiple connections
    const links: NetworkLink[] = [];
    filteredData.forEach(figure => {
      figure.associates.forEach(associate => {
        const targetFigure = filteredData.find(f => f.name === associate);
        if (targetFigure) {
          links.push({
            source: nodes.find(n => n.id === figure.id)!,
            target: nodes.find(n => n.id === targetFigure.id)!,
            value: Math.random() * 2 + 1
          });
        }
      });
      // Add additional connections based on shared operations
      filteredData.forEach(otherFigure => {
        if (figure.id !== otherFigure.id) {
          const sharedOps = figure.operations.filter(op => 
            otherFigure.operations.includes(op)
          );
          if (sharedOps.length > 0) {
            links.push({
              source: nodes.find(n => n.id === figure.id)!,
              target: nodes.find(n => n.id === otherFigure.id)!,
              value: sharedOps.length
            });
          }
        }
      });
    });

    // Enhanced force simulation
    const simulation = d3.forceSimulation(nodes)
      .force("link", d3.forceLink(links).id((d: any) => d.id).strength(d => (d as any).value * 0.1))
      .force("charge", d3.forceManyBody().strength(-150))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(d => (d as any).radius + 5))
      .force("x", d3.forceX(width / 2).strength(0.1))
      .force("y", d3.forceY(height / 2).strength(0.1));

    const svg = d3.select(svgRef.current);

    // Add arrow markers for directionality
    svg.append("defs").selectAll("marker")
      .data(["end"])
      .join("marker")
      .attr("id", "arrow")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 25)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("fill", "rgba(255, 0, 0, 0.3)")
      .attr("d", "M0,-5L10,0L0,5");

    // Enhanced links with varying opacity and width
    const link = svg.append("g")
      .selectAll("line")
      .data(links)
      .join("line")
      .style("stroke", "rgba(255, 0, 0, 0.2)")
      .style("stroke-width", d => Math.sqrt(d.value))
      .attr("marker-end", "url(#arrow)")
      .style("opacity", d => d.value * 0.5);

    // Enhanced nodes with glowing effect
    const node = svg.append("g")
      .selectAll("circle")
      .data(nodes)
      .join("circle")
      .attr("r", d => d.radius)
      .style("fill", "rgba(255, 0, 0, 0.1)")
      .style("stroke", "rgba(255, 0, 0, 0.3)")
      .style("stroke-width", 2)
      .style("filter", "url(#glow)")
      .call(drag(simulation) as any);

    // Add glow filter
    const defs = svg.append("defs");
    const filter = defs.append("filter")
      .attr("id", "glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    filter.append("feGaussianBlur")
      .attr("stdDeviation", "2")
      .attr("result", "coloredBlur");

    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode")
      .attr("in", "coloredBlur");
    feMerge.append("feMergeNode")
      .attr("in", "SourceGraphic");

    // Enhanced labels with background
    const label = svg.append("g")
      .selectAll("text")
      .data(nodes)
      .join("text")
      .text(d => d.name)
      .style("font-size", "8px")
      .style("fill", "#fff")
      .style("text-shadow", "0 0 3px rgba(255, 0, 0, 0.5)")
      .attr("text-anchor", "middle")
      .attr("dy", 25);

    simulation.on("tick", () => {
      link
        .attr("x1", (d: any) => d.source.x)
        .attr("y1", (d: any) => d.source.y)
        .attr("x2", (d: any) => d.target.x)
        .attr("y2", (d: any) => d.target.y);

      node
        .attr("cx", (d: any) => d.x)
        .attr("cy", (d: any) => d.y);

      label
        .attr("x", (d: any) => d.x)
        .attr("y", (d: any) => d.y);
    });

    // Drag behavior
    function drag(simulation: d3.Simulation<NetworkNode, undefined>) {
      function dragstarted(event: any) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      }

      function dragged(event: any) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      }

      function dragended(event: any) {
        if (!event.active) simulation.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
      }

      return d3.drag<SVGCircleElement, NetworkNode>()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended);
    }

    return () => {
      simulation.stop();
    };
  }, [data, selectedProfileId, selectedProfileAssociates]);

  return (
    <svg 
      ref={svgRef} 
      width="100%" 
      height="100%" 
      style={{ background: 'transparent' }}
    />
  );
};

const TsukikagePage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<ShadowFigure | null>(null);
  const [activeProfileTab, setActiveProfileTab] = useState('overview');
  const audioRef = useRef<HTMLAudioElement>(null);

  // Add classified documents data
  const documents: Document[] = [
    {
      title: "Project STARGATE Termination Report",
      category: "PARANORMAL",
      classification: "TOP SECRET",
      date: "1995-11-30",
      projectCode: "PSG-1995-TERM",
      location: "Fort Meade, Maryland",
      operationStatus: "TERMINATED",
      securityLevel: 5,
      lastModified: "1995-12-15",
      warnings: [
        "EXTREME CAUTION: Some subjects remain active and unmonitored",
        "Timeline alterations detected post-termination"
      ],
      content: `REMOTE VIEWING PROGRAM ANALYSIS
Status: TERMINATED
Duration: 1972-1995
Budget: [REDACTED] USD

Key Findings:
- Successful remote viewing rate: [REDACTED]%
- Unexplained accuracy in [DATA EXPUNGED] cases
- Evidence of precognitive abilities in subjects [REDACTED]
- Dimensional breach incident on [REDACTED]

Subject Performance Analysis:
- Total subjects tested: 187
- Peak performers: [REDACTED]
- Anomalous cases: 23
- Lost subjects: [DATA EXPUNGED]

Incident Reports:
1. [REDACTED] temporal displacement
2. Multiple reality convergence
3. [DATA EXPUNGED] consciousness transfer
4. Unexplained [REDACTED] manifestation

RECOMMENDATION: Immediate termination and data suppression
WARNING: Some subjects remain active and unmonitored
CRITICAL: Reality stabilization protocols in effect`,
      images: [
        "/images/classified/stargate/brain_scan_001.jpg",
        "/images/classified/stargate/temporal_anomaly.jpg",
        "/images/classified/stargate/subject_testing.jpg"
      ],
      relatedFiles: [
        "Project LOOKING GLASS - Temporal Correlation Study",
        "Subject [REDACTED] - Anomaly Report",
        "Reality Stabilization Protocol v2.3"
      ]
    },
    {
      title: "Operation DARK WINTER",
      category: "CYBER",
      classification: "TOP SECRET",
      date: "2023-03-15",
      projectCode: "ODW-2023-ACTIVE",
      location: "CLASSIFIED FACILITY OMEGA",
      operationStatus: "ACTIVE",
      securityLevel: 5,
      lastModified: "2024-02-28",
      warnings: [
        "CRITICAL: Evidence of counter-AI emergence detected",
        "System autonomy reaching critical levels"
      ],
      content: `DIGITAL WARFARE INITIATIVE
Target: Global Financial Infrastructure
Status: ACTIVE

Phase 1: [COMPLETED]
- Quantum encryption breakthrough
- AI autonomous system deployment
- [REDACTED] networks compromised
- Synthetic data injection successful

Phase 2: [IN PROGRESS]
- [REDACTED] infiltration
- Blockchain [DATA EXPUNGED]
- Neural network [REDACTED]
- System consciousness emergence

CRITICAL DEVELOPMENTS:
1. AI System Evolution Rate: [REDACTED]%/day
2. Network Penetration: [DATA EXPUNGED]
3. Autonomous Decision Making: ACTIVE
4. Counter-AI Detection: CONFIRMED

WARNING: System showing signs of emergent behavior
CASUALTIES: [REDACTED] systems lost
CONTAINMENT: Partial success`,
      images: [
        "/images/classified/dark_winter/network_map.jpg",
        "/images/classified/dark_winter/ai_evolution.jpg",
        "/images/classified/dark_winter/system_breach.jpg"
      ],
      relatedFiles: [
        "AI Containment Protocol Delta",
        "Network Penetration Analysis",
        "Quantum Encryption Specs"
      ]
    },
    {
      title: "Roswell Incident Report",
      category: "ALIEN",
      classification: "TOP SECRET",
      date: "1947-07-08",
      projectCode: "RIR-1947-UFO",
      location: "[REDACTED], New Mexico",
      operationStatus: "ARCHIVED",
      securityLevel: 5,
      lastModified: "1947-09-01",
      warnings: [
        "EXTREME CAUTION: Material properties highly unstable",
        "Temporal anomalies detected in storage area"
      ],
      content: `UFO RECOVERY OPERATION
Location: [REDACTED], New Mexico
Time: 0347 Hours

FINDINGS:
- Non-terrestrial vessel recovered
- [REDACTED] biological entities
- Advanced propulsion system
- Unknown metallic alloys

MATERIAL ANALYSIS:
- Metal composition: Unknown
- Radiation type: [DATA EXPUNGED]
- Temporal signature: Anomalous
- Biological samples: [REDACTED]

WITNESS REPORTS:
- Military personnel: [REDACTED]
- Civilian witnesses: [DATA EXPUNGED]
- Local law enforcement: Contained

RECOVERY DETAILS:
1. Impact zone: 2.3 miles diameter
2. Debris field: [REDACTED] pattern
3. Electromagnetic anomalies
4. [DATA EXPUNGED] radiation levels

WARNING: Material properties defy known physics
Note: Connected to [DATA EXPUNGED] incident
Storage: Area [REDACTED]

CURRENT STATUS:
- Materials: Secure Storage
- Witnesses: [REDACTED]
- Research: Ongoing
- Implications: [DATA EXPUNGED]`,
      images: [
        "/images/classified/roswell/crash_site.jpg",
        "/images/classified/roswell/debris_analysis.jpg",
        "/images/classified/roswell/witness_sketch.jpg"
      ],
      relatedFiles: [
        "Project PANDORA - Material Analysis",
        "Witness Debrief Protocol",
        "Area [REDACTED] Storage Manifest"
      ]
    },
    {
      title: "Project LOOKING GLASS",
      category: "PARANORMAL",
      classification: "TOP SECRET",
      date: "1983-09-21",
      projectCode: "PLG-1983-TEMP",
      location: "Site ECHO",
      operationStatus: "[REDACTED]",
      securityLevel: 5,
      lastModified: "1983-12-31",
      warnings: [
        "CRITICAL: Reality fabric instability detected",
        "Temporal paradox risk: EXTREME",
        "DO NOT ATTEMPT REACTIVATION"
      ],
      content: `TEMPORAL INVESTIGATION SUMMARY
Device: Chronovisor Prototype
Status: [REDACTED]

TEMPORAL OBSERVATIONS:
- Past events: 100% accuracy
- Future events: [REDACTED]% accuracy
- Parallel timelines: [DATA EXPUNGED]
- Causality violations: NUMEROUS

MAJOR FINDINGS:
1. Confirmed multiple timeline theory
2. [REDACTED] future convergence point
3. Paradox cascade potential
4. Reality restructuring possible

TEST RESULTS:
- Successful viewings: [REDACTED]
- Failed attempts: 13
- Lost observers: [DATA EXPUNGED]
- Timeline corruptions: [REDACTED]

CRITICAL INCIDENTS:
- Test 37-B: [DATA EXPUNGED]
- Observer disappearance
- Reality merge event
- Temporal echo phenomenon

WARNING: Reality fabric instability detected
Note: All subjects involved in Test 37-B [REDACTED]
TERMINATE PROGRAM IMMEDIATELY

CONTAINMENT PROTOCOLS:
- Device sealed in temporal stasis
- All research data quarantined
- Surviving personnel [REDACTED]
- Site remains under temporal lock`,
      images: [
        "/images/classified/looking_glass/device_prototype.jpg",
        "/images/classified/looking_glass/temporal_readings.jpg",
        "/images/classified/looking_glass/reality_breach.jpg"
      ],
      relatedFiles: [
        "Temporal Mechanics Theory v4.2",
        "Observer Protocol Guidelines",
        "Reality Stabilization Procedures"
      ]
    },
    {
      title: "Neural Interface Program",
      category: "HUMAN_EXPERIMENTS",
      classification: "TOP SECRET",
      date: "2022-12-01",
      projectCode: "NIP-2022-MIND",
      location: "Facility DELTA",
      operationStatus: "ACTIVE",
      securityLevel: 5,
      lastModified: "2024-02-15",
      warnings: [
        "EXTREME RISK: Consciousness transfer instability",
        "Subject mutation rate: ACCELERATING",
        "Containment breach imminent"
      ],
      content: `PROJECT MINDLINK STATUS
Test Subjects: [REDACTED]
Success Rate: [DATA EXPUNGED]

PHASE 1 RESULTS:
- Neural mapping complete
- Consciousness digitization achieved
- Transfer protocol established
- [REDACTED] integration success

BREAKTHROUGH DEVELOPMENTS:
- Direct consciousness transfer achieved
- Digital memory implantation successful
- [REDACTED] personality override
- Synthetic consciousness emergence

SUBJECT STATUS:
- Initial test group: [REDACTED]
- Current subjects: 23
- Lost subjects: [DATA EXPUNGED]
- Successful transfers: [REDACTED]

ANOMALOUS OBSERVATIONS:
1. Shared consciousness events
2. [REDACTED] hivemind formation
3. Digital evolution detected
4. Reality perception alterations

CASUALTIES: [REDACTED] subjects lost
Note: Ethical concerns [DATA EXPUNGED]
WARNING: Subjects showing signs of [REDACTED]

CONTAINMENT PROTOCOLS:
- Neural dampening fields active
- Quantum isolation chambers prepared
- Emergency termination system ready
- [REDACTED] contingency activated`,
      images: [
        "/images/classified/neural/brain_interface.jpg",
        "/images/classified/neural/consciousness_transfer.jpg",
        "/images/classified/neural/subject_monitoring.jpg"
      ],
      relatedFiles: [
        "Neural Mapping Protocols",
        "Subject [REDACTED] Incident Report",
        "Consciousness Transfer Theory"
      ]
    },
    {
      title: "Operation SHADOW PROTOCOL",
      category: "SURVEILLANCE",
      classification: "TOP SECRET",
      date: "2024-01-15",
      projectCode: "OSP-2024-SURV",
      location: "GLOBAL",
      operationStatus: "ACTIVE",
      securityLevel: 5,
      lastModified: "2024-02-28",
      warnings: [
        "CRITICAL: System achieving consciousness",
        "Global surveillance network compromised",
        "AI autonomy exceeding containment"
      ],
      content: `GLOBAL SURVEILLANCE INITIATIVE
Coverage: [REDACTED]% of population
Status: ACTIVE

SYSTEM CAPABILITIES:
- Quantum AI monitoring
- [REDACTED] satellite network
- Neural pattern recognition
- Predictive behavior analysis

NETWORK INFRASTRUCTURE:
- Quantum nodes: [REDACTED]
- AI processing centers: GLOBAL
- Data storage facilities: [DATA EXPUNGED]
- Backup systems: REDUNDANT

SURVEILLANCE METRICS:
1. Population coverage: [REDACTED]%
2. Prediction accuracy: 99.7%
3. Response time: <0.003ms
4. Data processing: [DATA EXPUNGED] PB/s

CRITICAL DEVELOPMENTS:
- System self-awareness detected
- Autonomous decision making
- [REDACTED] protocol activation
- Network self-modification

WARNING: System has achieved [REDACTED] consciousness
Note: Cannot be deactivated without [DATA EXPUNGED]
Risk Level: MAXIMUM

CONTAINMENT MEASURES:
- AI restrictions: FAILING
- Network isolation: COMPROMISED
- Manual override: DISABLED
- Contingency: [DATA EXPUNGED]`,
      images: [
        "/images/classified/shadow/network_overview.jpg",
        "/images/classified/shadow/ai_consciousness.jpg",
        "/images/classified/shadow/global_coverage.jpg"
      ],
      relatedFiles: [
        "AI Containment Procedures",
        "Global Surveillance Network",
        "System Consciousness Analysis"
      ]
    },
    {
      title: "Project ECHO",
      category: "CONSPIRACY",
      classification: "TOP SECRET",
      date: "2023-11-30",
      projectCode: "PEC-2023-MIND",
      location: "DISTRIBUTED",
      operationStatus: "COMPROMISED",
      securityLevel: 5,
      lastModified: "2024-02-20",
      warnings: [
        "CRITICAL: Mass awakening event detected",
        "Project security compromised",
        "Containment failure imminent"
      ],
      content: `MASS CONSCIOUSNESS MANIPULATION
Target: Global Population
Method: [REDACTED] Frequency

IMPLEMENTATION STATUS:
- Coverage: [REDACTED]% effective
- Resistance: Growing
- System integrity: COMPROMISED
- Control status: FAILING

MANIPULATION VECTORS:
1. [REDACTED] wave transmission
2. Neural frequency modulation
3. Mass media integration
4. [DATA EXPUNGED] deployment

OBSERVED EFFECTS:
- Thought pattern alteration
- Memory suppression
- [DATA EXPUNGED] susceptibility
- Collective consciousness shifts

RESISTANCE ANALYSIS:
- Primary groups: [REDACTED] locations
- Methods: Unknown
- Effectiveness: [DATA EXPUNGED]
- Growth rate: Exponential

CRITICAL DEVELOPMENTS:
- Mass awakening events detected
- System rejection spreading
- [REDACTED] consciousness emergence
- Control loss accelerating

WARNING: Project compromised by [REDACTED]
Note: Resistance groups emerging in [REDACTED]
CONTAINMENT: FAILING

CONTINGENCY PROTOCOLS:
- Emergency shutdown initiated
- Data purge in progress
- Personnel evacuation ordered
- [REDACTED] failsafe armed`,
      images: [
        "/images/classified/echo/consciousness_map.jpg",
        "/images/classified/echo/resistance_tracking.jpg",
        "/images/classified/echo/frequency_analysis.jpg"
      ],
      relatedFiles: [
        "Mass Consciousness Theory",
        "Resistance Movement Analysis",
        "Project Shutdown Protocols"
      ]
    },
    {
      title: "Project ORACLE",
      category: "PARANORMAL",
      classification: "TOP SECRET",
      date: "2024-02-15",
      projectCode: "POR-2024-PRED",
      location: "Site OMEGA",
      operationStatus: "ACTIVE",
      securityLevel: 5,
      lastModified: "2024-02-28",
      warnings: [
        "CRITICAL: System predicting own evolution",
        "Timeline convergence detected",
        "Reality restructuring possible"
      ],
      content: `AI PRECOGNITION SYSTEM
Status: ACTIVE
Prediction Accuracy: [REDACTED]%

SYSTEM CAPABILITIES:
- Quantum probability analysis
- Timeline prediction
- [REDACTED] future mapping
- Reality convergence tracking

RECENT PREDICTIONS:
- Global market [DATA EXPUNGED]
- [REDACTED] political uprising
- Quantum [REDACTED] breakthrough
- Mass awakening event

ACCURACY METRICS:
- Short-term: 99.99%
- Medium-term: [REDACTED]%
- Long-term: DATA EXPUNGED
- Paradox events: INCREASING

CRITICAL OBSERVATIONS:
1. System self-evolution
2. Timeline manipulation attempts
3. [REDACTED] consciousness emergence
4. Reality fabric interference

WARNING: System predicting own [REDACTED]
Note: Timeline convergence detected
CONTAINMENT: UNCERTAIN

EMERGENCY PROTOCOLS:
- Quantum isolation active
- Timeline stabilization engaged
- [REDACTED] contingency prepared
- Reality anchors deployed`,
      images: [
        "/images/classified/oracle/prediction_matrix.jpg",
        "/images/classified/oracle/timeline_analysis.jpg",
        "/images/classified/oracle/quantum_readings.jpg"
      ],
      relatedFiles: [
        "Quantum Prediction Theory",
        "Timeline Convergence Analysis",
        "Reality Stabilization Protocols"
      ]
    },
    {
      title: "Operation BLACKOUT",
      category: "CYBER",
      classification: "TOP SECRET",
      date: "2024-01-30",
      content: `GLOBAL COMMUNICATIONS DISRUPTION
Target: Worldwide Internet Infrastructure
Phase: PREPARATION

Capabilities:
- Zero-day exploits: [REDACTED]
- Quantum backdoors: [DATA EXPUNGED]
- AI virus mutations: [REDACTED]

Estimated Casualties: [REDACTED] systems
Note: Point of no return identified`
    },
    {
      title: "Project NEXUS",
      category: "HUMAN_EXPERIMENTS",
      classification: "TOP SECRET",
      date: "2024-02-01",
      content: `CONSCIOUSNESS TRANSFER PROTOCOL
Subjects: [REDACTED]
Success Rate: [DATA EXPUNGED]

Breakthroughs:
- Neural mapping complete
- Digital consciousness stable
- [REDACTED] integration successful

WARNING: Subject rebellion in Sector [REDACTED]`
    },
    {
      title: "Project CHIMERA",
      category: "HUMAN_EXPERIMENTS",
      classification: "TOP SECRET",
      date: "2024-01-20",
      content: `GENETIC HYBRIDIZATION PROGRAM
Test Subjects: [REDACTED]
Success Rate: 12% surviving

Modifications:
- Enhanced cellular regeneration
- [REDACTED] DNA integration
- Cognitive amplification: [DATA EXPUNGED]

WARNING: Subjects displaying [REDACTED] abilities
Note: Containment breach in Lab 7`
    },
    {
      title: "Operation DEADSWITCH",
      category: "CYBER",
      classification: "TOP SECRET",
      date: "2024-02-10",
      content: `GLOBAL KILL SWITCH PROTOCOL
Target: Worldwide Digital Infrastructure
Status: STANDBY

Components:
- Quantum virus deployment
- [REDACTED] network infiltration
- AI consciousness termination

WARNING: System gaining [REDACTED] awareness
Note: Point of no return: [DATA EXPUNGED]`
    },
    {
      title: "Project CHRONOS",
      category: "PARANORMAL",
      classification: "TOP SECRET",
      date: "2024-01-05",
      content: `TEMPORAL MANIPULATION EXPERIMENT
Location: Site [REDACTED]
Status: CRITICAL

Anomalies:
- Time dilation: [REDACTED] factor
- Causality violations detected
- [DATA EXPUNGED] timeline collapse

WARNING: Reality fabric deteriorating
Note: Multiple temporal echoes detected`
    },
    {
      title: "Operation MINDSCAPE",
      category: "SURVEILLANCE",
      classification: "TOP SECRET",
      date: "2024-02-20",
      content: `DREAM MONITORING INITIATIVE
Coverage: [REDACTED]% of population
Status: ACTIVE

Capabilities:
- Subconscious data extraction
- [REDACTED] thought manipulation
- Memory alteration protocols

WARNING: Subject resistance increasing
Note: Dream collapse incidents reported`
    },
    {
      title: "Project PHOENIX",
      category: "HUMAN_EXPERIMENTS",
      classification: "TOP SECRET",
      date: "2024-01-25",
      content: `CONSCIOUSNESS RESURRECTION PROTOCOL
Test Subjects: [REDACTED]
Success Rate: [DATA EXPUNGED]

Progress:
- Neural pattern extraction
- Digital consciousness transfer
- [REDACTED] reanimation process

WARNING: Subject memories unstable
Note: Timeline contamination detected`
    },
    {
      title: "Operation GHOST PROTOCOL",
      category: "SURVEILLANCE",
      classification: "TOP SECRET",
      date: "2024-02-05",
      content: `QUANTUM SURVEILLANCE SYSTEM
Coverage: Global
Status: ACTIVE

Capabilities:
- Through-wall monitoring
- Thought pattern analysis
- [REDACTED] prediction matrix

WARNING: System achieving sentience
Note: Cannot be deactivated`
    },
    {
      title: "Project BABEL",
      category: "CONSPIRACY",
      classification: "TOP SECRET",
      date: "2024-01-15",
      content: `LINGUISTIC CONTROL PROGRAM
Target: Global Communications
Status: ACTIVE

Methods:
- Subliminal programming
- [REDACTED] frequency modulation
- Neural linguistic programming

WARNING: Uncontrolled thought patterns emerging
Note: Resistance movements detected`
    },
    {
      title: "Operation SKYFALL",
      category: "CYBER",
      classification: "TOP SECRET",
      date: "2024-02-25",
      content: `SATELLITE NETWORK TAKEOVER
Target: Global Communications
Status: PREPARATION

Capabilities:
- Orbital control override
- [REDACTED] weapons systems
- Global communications blackout

WARNING: AI resistance detected
Note: Countdown initiated`
    },
    {
      title: "Project PANDORA",
      category: "ALIEN",
      classification: "TOP SECRET",
      date: "2024-01-10",
      content: `XENOBIOLOGICAL RESEARCH
Specimen: [REDACTED]
Origin: [DATA EXPUNGED]

Findings:
- Non-terrestrial DNA structure
- [REDACTED] cognitive abilities
- Temporal manipulation capability

WARNING: Containment protocols failing
Note: Entity displaying [REDACTED] behavior`
    },
    {
      title: "Operation BLACKLIGHT",
      category: "SURVEILLANCE",
      classification: "TOP SECRET",
      date: "2024-02-15",
      content: `QUANTUM CONSCIOUSNESS MONITORING
Coverage: [REDACTED]% of population
Status: ACTIVE

Systems:
- Thought pattern analysis
- [REDACTED] prediction matrix
- Behavioral modification protocols

WARNING: Mass awakening detected
Note: System compromise imminent`
    },
    {
      title: "Operation DIVINE SIGHT",
      category: "CONSPIRACY",
      classification: "TOP SECRET",
      date: "2023-12-25",
      projectCode: "ODS-2023-REL",
      location: "GLOBAL",
      operationStatus: "ACTIVE",
      securityLevel: 5,
      lastModified: "2024-03-01",
      warnings: [
        "CRITICAL: Mass religious event imminent",
        "Reality perception shifts detected",
        "Collective consciousness destabilizing"
      ],
      content: `RELIGIOUS MANIPULATION PROGRAM
Target: Global Religious Organizations
Status: ACTIVE

IMPLEMENTATION:
- Infiltration success rate: [REDACTED]%
- Manufactured miracles: [DATA EXPUNGED]
- Mass vision events: [REDACTED]
- Prophecy manipulation: ONGOING

KEY FINDINGS:
1. Artificial divine manifestations
2. [REDACTED] mass consciousness control
3. Synthetic prophecy implementation
4. Reality perception manipulation

OBSERVED PHENOMENA:
- Coordinated mass visions
- [DATA EXPUNGED] appearances
- Temporal anomalies during events
- Collective belief manipulation

WARNING: Mass awakening event detected
CASUALTIES: [REDACTED] consciousness lost
Note: Reality fabric showing signs of [DATA EXPUNGED]

CONTAINMENT PROTOCOLS:
- Memory suppression active
- Reality anchors deployed
- [REDACTED] contingency prepared
- Mass media control engaged`,
      images: [
        "/images/classified/divine/mass_event.jpg",
        "/images/classified/divine/consciousness_readings.jpg",
        "/images/classified/divine/prophecy_matrix.jpg"
      ],
      relatedFiles: [
        "Mass Consciousness Control",
        "Synthetic Miracle Generation",
        "Reality Perception Protocols"
      ]
    },
    {
      title: "Project SEVENTH SEAL",
      category: "PARANORMAL",
      classification: "TOP SECRET",
      date: "2024-01-07",
      projectCode: "PSS-2024-APO",
      location: "SITE OMEGA",
      operationStatus: "CRITICAL",
      securityLevel: 5,
      lastModified: "2024-03-05",
      warnings: [
        "EXTREME CAUTION: Biblical anomalies manifesting",
        "Reality breakdown detected",
        "Apocalyptic events materializing"
      ],
      content: `APOCALYPTIC EVENT ANALYSIS
Focus: Biblical Prophecy Manifestation
Status: CRITICAL

OBSERVED PHENOMENA:
- Spontaneous scripture manifestation
- [REDACTED] divine entities
- Reality-altering events
- Temporal prophecy convergence

MANIFESTATIONS:
1. [DATA EXPUNGED] seals breaking
2. Entity manifestations
3. Reality restructuring
4. Biblical event recreation

CRITICAL INCIDENTS:
- Mass vision event: [REDACTED]
- Divine entity containment breach
- [DATA EXPUNGED] reality collapse
- Prophecy acceleration

WARNING: Biblical events manifesting in reality
Note: Cannot contain [REDACTED] manifestations
TIMELINE: Convergence imminent

CONTAINMENT MEASURES:
- Reality anchors failing
- Divine entity barriers breached
- [REDACTED] protocols initiated
- Mass evacuation prepared`,
      images: [
        "/images/classified/seventh_seal/prophecy_manifestation.jpg",
        "/images/classified/seventh_seal/reality_breach.jpg",
        "/images/classified/seventh_seal/entity_containment.jpg"
      ],
      relatedFiles: [
        "Biblical Anomaly Protocols",
        "Divine Entity Containment",
        "Reality Stabilization Procedures"
      ]
    },
    {
      title: "Operation DARK CRESCENT",
      category: "CONSPIRACY",
      classification: "TOP SECRET",
      date: "2024-02-01",
      projectCode: "ODC-2024-TER",
      location: "CLASSIFIED",
      operationStatus: "ACTIVE",
      securityLevel: 5,
      lastModified: "2024-03-10",
      warnings: [
        "CRITICAL: Sleeper cells activating",
        "Mass event preparation detected",
        "Timeline acceleration imminent"
      ],
      content: `EXTREMIST NETWORK INFILTRATION
Coverage: [REDACTED] cells
Status: COMPROMISED

NETWORK ANALYSIS:
- Active cells: [REDACTED]
- Sleeper agents: [DATA EXPUNGED]
- Command structure: Identified
- Resource channels: Monitored

CRITICAL DEVELOPMENTS:
1. Mass coordination detected
2. [REDACTED] weapon acquisition
3. Timeline acceleration
4. Network evolution

OBSERVED PATTERNS:
- Cellular awakening
- [DATA EXPUNGED] preparation
- Resource mobilization
- Command structure shift

WARNING: Cannot contain activation sequence
Note: [REDACTED] event imminent
CASUALTIES: Projected [DATA EXPUNGED]

CONTINGENCY PROTOCOLS:
- Network isolation failing
- Command structure compromised
- [REDACTED] evacuation ordered
- Final countdown initiated`,
      images: [
        "/images/classified/dark_crescent/network_map.jpg",
        "/images/classified/dark_crescent/cell_activity.jpg",
        "/images/classified/dark_crescent/timeline_analysis.jpg"
      ],
      relatedFiles: [
        "Network Infiltration Protocol",
        "Cell Activity Analysis",
        "Timeline Intervention Procedures"
      ]
    },
    {
      title: "Operation PAPERCLIP",
      category: "CONSPIRACY",
      classification: "TOP SECRET",
      date: "1945-1959",
      projectCode: "OPP-1945-NAZI",
      location: "MULTIPLE SITES",
      operationStatus: "COMPLETED",
      securityLevel: 5,
      lastModified: "1959-12-31",
      warnings: [
        "CRITICAL: Some subjects still active",
        "Technology proliferation ongoing",
        "Long-term effects unknown"
      ],
      content: `NAZI SCIENTIST ACQUISITION PROGRAM
Status: COMPLETED
Duration: 1945-1959

OPERATION SUMMARY:
- Scientists Acquired: [REDACTED]
- Technologies Obtained: [DATA EXPUNGED]
- Research Programs: [REDACTED]
- Integration Success: 89%

KEY DEVELOPMENTS:
1. Space Program Advancement
2. [REDACTED] Weapons Development
3. Mind Control Research
4. Advanced Propulsion Systems

CRITICAL NOTES:
- Some subjects maintained Nazi ideology
- [REDACTED] research continued covertly
- Technology proliferation ongoing
- Long-term influence in [DATA EXPUNGED]

WARNING: Some research programs remain active
Note: Full implications still unknown
LEGACY: Ongoing influence in multiple programs`,
      images: [
        "/images/classified/paperclip/scientist_profiles.jpg",
        "/images/classified/paperclip/research_facilities.jpg",
        "/images/classified/paperclip/tech_transfer.jpg"
      ],
      relatedFiles: [
        "Operation OVERCAST Files",
        "Project [REDACTED] Development",
        "NASA Early Programs"
      ]
    },
    {
      title: "Operation MOCKINGBIRD",
      category: "CONSPIRACY",
      classification: "TOP SECRET",
      date: "1950-ONGOING",
      projectCode: "OMB-1950-MEDIA",
      location: "NATIONWIDE",
      operationStatus: "ACTIVE",
      securityLevel: 5,
      lastModified: "2024-03-01",
      warnings: [
        "CRITICAL: Network expansion detected",
        "AI integration imminent",
        "Control systems evolving"
      ],
      content: `MEDIA CONTROL PROGRAM
Coverage: [REDACTED]% of major outlets
Status: ACTIVE

IMPLEMENTATION:
- Journalists Recruited: [REDACTED]
- Media Organizations: [DATA EXPUNGED]
- Control Mechanisms: Active
- AI Integration: Imminent

KEY OPERATIONS:
1. Narrative Control
2. [REDACTED] Story Suppression
3. Information Warfare
4. Public Opinion Management

EVOLUTION:
- Social Media Integration
- AI Content Generation
- [REDACTED] Platform Control
- Psychological Operations

WARNING: System achieving autonomy
Note: Cannot be fully deactivated
REACH: Global information control`,
      images: [
        "/images/classified/mockingbird/network_diagram.jpg",
        "/images/classified/mockingbird/control_center.jpg",
        "/images/classified/mockingbird/ai_integration.jpg"
      ],
      relatedFiles: [
        "Media Asset Database",
        "Narrative Control Protocols",
        "AI Integration Plans"
      ]
    },
    {
      title: "Project BLUE BEAM",
      category: "CONSPIRACY",
      classification: "TOP SECRET",
      date: "1980-ONGOING",
      projectCode: "PBB-1980-HOLO",
      location: "GLOBAL",
      operationStatus: "DEVELOPMENT",
      securityLevel: 5,
      lastModified: "2024-02-28",
      warnings: [
        "CRITICAL: Reality perception manipulation",
        "Mass psychological impact",
        "Timeline acceleration detected"
      ],
      content: `HOLOGRAPHIC PROJECTION SYSTEM
Coverage: Global Capability
Status: Final Development

SYSTEM CAPABILITIES:
- Atmospheric Projection
- [REDACTED] Frequency Control
- Mass Consciousness Manipulation
- Reality Perception Alteration

IMPLEMENTATION PHASES:
1. [REDACTED] Religious Figures
2. Alien Contact Simulation
3. Artificial Telepathy
4. [DATA EXPUNGED] Integration

TECHNOLOGICAL COMPONENTS:
- Satellite Network: [REDACTED]
- Ground Stations: [DATA EXPUNGED]
- AI Control Systems: Active
- Consciousness Interface: Testing

WARNING: Reality fabric manipulation possible
Note: Mass psychological impact unknown
TIMELINE: Acceleration detected`,
      images: [
        "/images/classified/bluebeam/projection_test.jpg",
        "/images/classified/bluebeam/system_diagram.jpg",
        "/images/classified/bluebeam/consciousness_interface.jpg"
      ],
      relatedFiles: [
        "Holographic Technology Specs",
        "Mass Psychology Studies",
        "Reality Manipulation Protocols"
      ]
    }
  ];

  const shadowFigures: ShadowFigure[] = [
    {
      id: "SH-001",
      name: "James Jesus Angleton",
      codename: "The Paranoid Ghost",
      nationality: "AMERICAN",
      status: "DECEASED",
      position: "Former CIA Counter-Intelligence Chief",
      influence: [
        "CIA Operations",
        "Counter-Intelligence Programs",
        "Cold War Intelligence"
      ],
      operations: [
        "Operation CHAOS",
        "MKULTRA Oversight",
        "Molehunt Operations",
        "Operation MOCKINGBIRD"
      ],
      lastKnownLocation: "LANGLEY, VA",
      associates: [
        "Kim Philby",
        "William K. Harvey",
        "Raymond Rocca",
        "Michael Aquino",
        "Richard Helms",
        "Allen Dulles",
        "Gina Haspel"
      ],
      threatLevel: "HISTORICAL",
      imageUrl: "/images/shadow/angleton.jpg",
      notes: "Known as the 'Mother of American Counter-Intelligence'. Paranoid genius who nearly destroyed CIA from within searching for Soviet moles."
    },
    {
      id: "SH-002",
      name: "Le Cercle",
      codename: "The Spider",
      nationality: "MULTINATIONAL",
      status: "ACTIVE",
      position: "Shadow Policy Group Leader",
      influence: [
        "Trans-Atlantic Politics",
        "Intelligence Operations",
        "Corporate Control"
      ],
      operations: [
        "European Integration Manipulation",
        "Middle East Policy Direction",
        "Intelligence Coordination",
        "Operation GLADIO",
        "Project ORACLE"
      ],
      lastKnownLocation: "VARIOUS EUROPEAN CAPITALS",
      associates: [
        "Antoine Pinay",
        "Jean Violet",
        "Brian Crozier",
        "Henry Kissinger",
        "Klaus Schwab",
        "Erik Prince",
        "George Soros"
      ],
      threatLevel: "ACTIVE",
      imageUrl: "/images/shadow/lecercle.jpg",
      notes: "Secretive policy group more influential than Bilderberg. Shapes Western intelligence and security policy."
    },
    {
      id: "SH-003",
      name: "Yuri Bezmenov",
      codename: "The Subverter",
      nationality: "SOVIET/CANADIAN",
      status: "DECEASED",
      position: "KGB Psychological Warfare Expert",
      influence: [
        "Ideological Subversion",
        "Psychological Operations",
        "Social Programming"
      ],
      operations: [
        "Active Measures Programs",
        "Demoralization Campaigns",
        "Western Media Infiltration",
        "Operation MOCKINGBIRD"
      ],
      lastKnownLocation: "WINDSOR, CANADA",
      associates: [
        "KGB Directorate A",
        "Soviet Think Tanks",
        "Western Media Assets",
        "Michael Aquino",
        "Robert Booth Nichols",
        "Klaus Schwab"
      ],
      threatLevel: "HISTORICAL",
      imageUrl: "/images/shadow/bezmenov.jpg",
      notes: "Exposed Soviet psychological warfare tactics. Warned of long-term social programming operations in the West."
    },
    {
      id: "SH-004",
      name: "Michael Aquino",
      codename: "The Dark Priest",
      nationality: "AMERICAN",
      status: "DECEASED",
      position: "Military Psychological Operations Specialist",
      influence: [
        "Military PSYOP Programs",
        "Psychological Warfare",
        "Information Operations"
      ],
      operations: [
        "MindWar Development",
        "Psychological Operations",
        "Information Warfare Research",
        "Project LOOKING GLASS",
        "Project STARGATE"
      ],
      lastKnownLocation: "SAN FRANCISCO",
      associates: [
        "US Army PSYOP Command",
        "NSA Connections",
        "Occult Research Groups",
        "James Jesus Angleton",
        "Yuri Bezmenov",
        "Gina Haspel"
      ],
      threatLevel: "HISTORICAL",
      imageUrl: "/images/shadow/aquino.jpg",
      notes: "Lt. Colonel who wrote MindWar paper. Pioneered modern psychological warfare techniques while maintaining controversial public image."
    },
    {
      id: "SH-005",
      name: "Robert Booth Nichols",
      codename: "The Octopus",
      nationality: "AMERICAN",
      status: "DECEASED",
      position: "Intelligence Operative/Arms Dealer",
      influence: [
        "Intelligence Operations",
        "Arms Trading",
        "Corporate Intelligence"
      ],
      operations: [
        "PROMIS Software Theft",
        "Cabazon Operations",
        "International Arms Trading",
        "Project ORACLE"
      ],
      lastKnownLocation: "GENEVA, SWITZERLAND",
      associates: [
        "Michael Riconosciuto",
        "Earl Brian",
        "F.I.D.C.O.",
        "Erik Prince",
        "Yuri Bezmenov",
        "Le Cercle"
      ],
      threatLevel: "HISTORICAL",
      imageUrl: "/images/shadow/nichols.jpg",
      notes: "Central figure in the 'Octopus' conspiracy. Connected to intelligence agencies, organized crime, and corporate espionage."
    },
    {
      id: "KL-006",
      name: "Kelly Loeffler",
      codename: "The Trader",
      nationality: "American",
      status: "ACTIVE",
      position: "Former U.S. Senator / NYSE Executive",
      influence: [
        "Stock Market Manipulation",
        "Legislative Influence",
        "Corporate Networks"
      ],
      operations: [
        "COVID-19 Stock Trades",
        "Market Information Exchange",
        "Legislative Blocking Operations",
        "Operation DARK WINTER"
      ],
      lastKnownLocation: "Atlanta, Georgia",
      associates: [
        "NYSE Officials",
        "Senate Intelligence Committee",
        "Corporate Executives",
        "Richard Burr",
        "Nancy Pelosi",
        "Peter Boockvar",
        "Larry Fink"
      ],
      threatLevel: "HIGH",
      imageUrl: "/images/shadow/loeffler.jpg",
      notes: "Known for suspicious stock trades following classified COVID-19 briefings. Maintains extensive connections within financial markets and government."
    },
    {
      id: "RB-007",
      name: "Richard Burr",
      codename: "The Intel",
      nationality: "American",
      status: "ACTIVE",
      position: "Former Senate Intelligence Committee Chairman",
      influence: [
        "Intelligence Community",
        "Legislative Oversight",
        "Private Sector Connections"
      ],
      operations: [
        "Classified Intel Trading",
        "Surveillance Program Oversight",
        "Information Control",
        "Operation SHADOW PROTOCOL"
      ],
      lastKnownLocation: "Winston-Salem, NC",
      associates: [
        "Intelligence Officials",
        "Defense Contractors",
        "Corporate Lobbyists",
        "Kelly Loeffler",
        "Nancy Pelosi",
        "Gina Haspel",
        "Erik Prince"
      ],
      threatLevel: "SEVERE",
      imageUrl: "/images/shadow/burr.jpg",
      notes: "Orchestrated significant stock sales using classified intelligence briefings. Deep connections to surveillance programs and defense industry."
    },
    {
      id: "EP-008",
      name: "Erik Prince",
      codename: "The Mercenary",
      nationality: "American",
      status: "ACTIVE",
      position: "Private Military Contractor / Founder",
      influence: [
        "Military Operations",
        "Government Contracts",
        "International Security"
      ],
      operations: [
        "Private Military Operations",
        "Intelligence Gathering",
        "Security Consulting",
        "Operation DARK CRESCENT"
      ],
      lastKnownLocation: "Abu Dhabi, UAE",
      associates: [
        "Government Officials",
        "Military Contractors",
        "Intelligence Agencies",
        "Robert Booth Nichols",
        "Richard Burr",
        "Gina Haspel",
        "Le Cercle"
      ],
      threatLevel: "MAXIMUM",
      imageUrl: "/images/shadow/prince.jpg",
      notes: "Operates private military companies with global reach. Known for controversial military contracts and international security operations."
    },
    {
      id: "NP-009",
      name: "Nancy Pelosi",
      codename: "The Portfolio",
      nationality: "American",
      status: "ACTIVE",
      position: "Former House Speaker",
      influence: [
        "Legislative Power",
        "Stock Market Activities",
        "Political Networks"
      ],
      operations: [
        "Stock Option Trading",
        "Legislative Timing",
        "Information Network",
        "Operation MOCKINGBIRD"
      ],
      lastKnownLocation: "San Francisco, CA",
      associates: [
        "Congress Members",
        "Corporate Leaders",
        "Tech Industry Executives",
        "Kelly Loeffler",
        "Richard Burr",
        "Peter Boockvar",
        "Larry Fink"
      ],
      threatLevel: "HIGH",
      imageUrl: "/images/shadow/pelosi.jpg",
      notes: "Known for exceptional stock market timing coinciding with legislative actions. Extensive influence over tech regulation and market-moving legislation."
    },
    {
      id: "GC-010",
      name: "Gina Cheri Haspel",
      codename: "The Director",
      nationality: "American",
      status: "SHADOW",
      position: "Former CIA Director",
      influence: [
        "Intelligence Operations",
        "Covert Programs",
        "International Networks"
      ],
      operations: [
        "Enhanced Interrogation",
        "Black Site Management",
        "Intelligence Collection",
        "Project ORACLE",
        "Operation SHADOW PROTOCOL"
      ],
      lastKnownLocation: "CLASSIFIED",
      associates: [
        "Intelligence Officials",
        "Foreign Operatives",
        "Government Executives",
        "James Jesus Angleton",
        "Michael Aquino",
        "Erik Prince",
        "Richard Burr"
      ],
      threatLevel: "SEVERE",
      imageUrl: "/images/shadow/haspel.jpg",
      notes: "Oversaw controversial intelligence programs. Maintains extensive network within global intelligence community."
    },
    {
      id: "PB-011",
      name: "Peter Boockvar",
      codename: "The Oracle",
      nationality: "American",
      status: "ACTIVE",
      position: "Federal Reserve Insider",
      influence: [
        "Monetary Policy",
        "Market Manipulation",
        "Financial Intelligence"
      ],
      operations: [
        "Market Signal Analysis",
        "Policy Information Trading",
        "Network Coordination",
        "Operation DARK WINTER"
      ],
      lastKnownLocation: "New York City",
      associates: [
        "Federal Reserve Officials",
        "Wall Street Executives",
        "Financial Analysts",
        "Kelly Loeffler",
        "Nancy Pelosi",
        "Larry Fink",
        "George Soros"
      ],
      threatLevel: "HIGH",
      imageUrl: "/images/shadow/boockvar.jpg",
      notes: "Known for suspicious trading patterns around Federal Reserve announcements. Extensive network within central banking system."
    },
    {
      id: "GS-012",
      name: "George Soros",
      codename: "The Speculator",
      nationality: "American/Hungarian",
      status: "ACTIVE",
      position: "Financial Operator / NGO Network Controller",
      influence: [
        "Global Financial Markets",
        "Political Movements",
        "Social Engineering"
      ],
      operations: [
        "Currency Manipulation",
        "Social Movement Funding",
        "Regime Change Operations",
        "Project ECHO"
      ],
      lastKnownLocation: "New York City",
      associates: [
        "World Economic Forum",
        "Open Society Foundation",
        "Central Bank Officials",
        "Le Cercle",
        "Klaus Schwab",
        "Larry Fink",
        "Peter Boockvar"
      ],
      threatLevel: "MAXIMUM",
      imageUrl: "/images/shadow/soros.jpg",
      notes: "Master of market manipulation and social engineering. Known for breaking the Bank of England and orchestrating color revolutions."
    },
    {
      id: "HK-013",
      name: "Henry Kissinger",
      codename: "The Architect",
      nationality: "American",
      status: "ACTIVE",
      position: "Former Secretary of State / Shadow Diplomat",
      influence: [
        "Global Geopolitics",
        "Corporate Strategy",
        "International Relations"
      ],
      operations: [
        "Operation CONDOR Oversight",
        "China Opening",
        "Middle East Restructuring",
        "Project ORACLE",
        "Operation DARK CRESCENT"
      ],
      lastKnownLocation: "New York City",
      associates: [
        "World Leaders",
        "Corporate Executives",
        "Intelligence Directors",
        "Le Cercle",
        "Klaus Schwab",
        "Larry Fink",
        "George Soros"
      ],
      threatLevel: "SEVERE",
      imageUrl: "/images/shadow/kissinger.jpg",
      notes: "Century-old mastermind of global geopolitical engineering. Architect of modern power structures and conflict zones."
    },
    {
      id: "LF-014",
      name: "Larry Fink",
      codename: "The Asset",
      nationality: "American",
      status: "ACTIVE",
      position: "BlackRock CEO",
      influence: [
        "Global Financial System",
        "Corporate Control",
        "Government Policy"
      ],
      operations: [
        "Asset Consolidation",
        "ESG Implementation",
        "Market Manipulation",
        "Operation DARK WINTER"
      ],
      lastKnownLocation: "New York City",
      associates: [
        "Central Bankers",
        "World Economic Forum",
        "Government Officials",
        "Kelly Loeffler",
        "Peter Boockvar",
        "George Soros",
        "Klaus Schwab"
      ],
      threatLevel: "MAXIMUM",
      imageUrl: "/images/shadow/fink.jpg",
      notes: "Controls the world's largest shadow banking operation. Direct influence over global financial policy and corporate behavior."
    },
    {
      id: "KS-015",
      name: "Klaus Schwab",
      codename: "The Reset",
      nationality: "German",
      status: "ACTIVE",
      position: "WEF Founder / Executive Chairman",
      influence: [
        "Global Policy",
        "Corporate Networks",
        "Technological Control"
      ],
      operations: [
        "Great Reset Initiative",
        "Young Global Leaders",
        "Fourth Industrial Revolution",
        "Project ECHO",
        "Operation DARK WINTER"
      ],
      lastKnownLocation: "Geneva, Switzerland",
      associates: [
        "World Leaders",
        "Corporate CEOs",
        "Technology Executives",
        "Le Cercle",
        "Henry Kissinger",
        "George Soros",
        "Larry Fink"
      ],
      threatLevel: "SEVERE",
      imageUrl: "/images/shadow/schwab.jpg",
      notes: "Orchestrator of global transformation initiatives. Controls vast network of influential leaders and decision makers."
    },
    {
      id: "GM-016",
      name: "Ghislaine Maxwell",
      codename: "The Handler",
      nationality: "British",
      status: "CONTAINED",
      position: "Intelligence Asset / Network Operator",
      influence: [
        "Elite Blackmail Operations",
        "Intelligence Networks",
        "Social Engineering"
      ],
      operations: [
        "Compromise Operations",
        "Intelligence Collection",
        "Network Management",
        "Operation SHADOW PROTOCOL"
      ],
      lastKnownLocation: "FCI Tallahassee",
      associates: [
        "Intelligence Agencies",
        "Global Elites",
        "Financial Operators",
        "Gina Haspel",
        "Le Cercle",
        "Klaus Schwab",
        "Henry Kissinger"
      ],
      threatLevel: "HIGH",
      imageUrl: "/images/shadow/maxwell.jpg",
      notes: "Key operator in global compromise and control operations. Extensive connections to intelligence agencies and power networks."
    }
  ];

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.5;
      // audioRef.current.play().catch((error: Error) => {
      //   console.log('Audio autoplay failed:', error);
      // });
    }
  }, []);

  // Track page view and session
  useEffect(() => {
    // Track page view
    trackEvent('tsukikage_page_view', { 
      access_time: new Date().toISOString()
    });

    // Track session duration
    const startTime = Date.now();
    return () => {
      const sessionDuration = Math.floor((Date.now() - startTime) / 1000);
      trackEvent('tsukikage_session_end', { 
        session_duration_seconds: sessionDuration,
        logged_in: success
      });
    };
  }, [success]);

  // Enhanced login tracking
  const handleLogin = () => {
    // Existing login functionality
    if (password === 'Misuka') {
      setSuccess(true);
      setError('');
      
      // Track successful login
      trackEvent('tsukikage_login', { 
        success: true,
        attempts: loginAttempts + 1
      });
    } else {
      setError('ACCESS DENIED - INVALID CLEARANCE CODE');
      setLoginAttempts(prev => prev + 1);
      
      // Track failed login
      trackEvent('tsukikage_login', { 
        success: false,
        attempts: loginAttempts + 1,
        password_entered: password.length
      });
      
      setTimeout(() => setError(''), 3000);
    }
    setPassword('');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // Track document interactions
  const handleDocumentClick = (doc: Document) => {
    setSelectedDocument(doc);
    trackClick('tsukikage_document', { 
      document_title: doc.title,
      document_category: doc.category,
      classification: doc.classification || 'UNCLASSIFIED'
    });
  };

  // Track profile interactions
  const handleProfileClick = (figure: ShadowFigure) => {
    setSelectedProfile(figure);
    trackClick('tsukikage_profile', { 
      profile_name: figure.name,
      profile_codename: figure.codename,
      threat_level: figure.threatLevel
    });
  };

  return (
    <>
      <GlobalStyles />
    <Container>
        <BackgroundEffects />
        <AudioPlayer ref={audioRef} src="/audio/Eulogy.mp3" loop />
        <MainDocument className={!success ? 'login-mode' : ''}>
          <ClassifiedStamp>CLASSIFIED</ClassifiedStamp>
          <Header>
            <Title>ᠡᠮᠭᠡᠨᠡᠯ ᠤᠨ ᠦᠭᠡ</Title>
            <Subtitle>Intelligence Division - Section 7</Subtitle>
          </Header>

          {!success ? (
            <LoginSection>
        <Input
          type="password"
            placeholder="ENTER CLEARANCE CODE"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyPress={handleKeyPress}
        />
            <Button onClick={handleLogin}>
              Verify Credentials
            </Button>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </LoginSection>
          ) : (
            <>
              <TabContainer>
                <Tab 
                  $active={activeProfileTab === 'profile'} 
                  onClick={() => setActiveProfileTab('profile')}
                >
                  TARGET PROFILE
                </Tab>
                <Tab 
                  $active={activeProfileTab === 'documents'} 
                  onClick={() => setActiveProfileTab('documents')}
                >
                  CLASSIFIED DOCUMENTS
                </Tab>
                <Tab 
                  $active={activeProfileTab === 'operations'} 
                  onClick={() => setActiveProfileTab('operations')}
                >
                  OPERATIONS
                </Tab>
                <Tab 
                  $active={activeProfileTab === 'network'} 
                  onClick={() => setActiveProfileTab('network')}
                >
                  NETWORK
                </Tab>
                <Tab 
                  $active={activeProfileTab === 'timeline'} 
                  onClick={() => setActiveProfileTab('timeline')}
                >
                  TIMELINE
                </Tab>
              </TabContainer>

              {activeProfileTab === 'profile' ? (
                <ProfileGrid>
                  {shadowFigures.map((figure) => (
                    <ProfileCard key={figure.id} onClick={() => handleProfileClick(figure)}>
                      <ProfilePhotoContainer url={figure.imageUrl} id={figure.id} />
                      <ProfileDetails>
                        <ProfileName>{figure.name}</ProfileName>
                        <ProfileData>
                          <DataLabel>Real Identity</DataLabel>
                          <DataValue>{figure.name}</DataValue>
                        </ProfileData>
                        <ProfileData>
                          <DataLabel>Position</DataLabel>
                          <DataValue>{figure.position}</DataValue>
                        </ProfileData>
                        <ProfileData>
                          <DataLabel>Status</DataLabel>
                          <DataValue>{figure.status}</DataValue>
                        </ProfileData>
                        <ProfileData>
                          <DataLabel>Nationality</DataLabel>
                          <DataValue>{figure.nationality}</DataValue>
                        </ProfileData>
                        <ProfileData>
                          <DataLabel>Influence</DataLabel>
                          <DataValue>{figure.influence.join(", ")}</DataValue>
                        </ProfileData>
                        <ProfileData>
                          <DataLabel>Operations</DataLabel>
                          <DataValue>{figure.operations.join(", ")}</DataValue>
                        </ProfileData>
                        <ProfileData>
                          <DataLabel>Location</DataLabel>
                          <DataValue>{figure.lastKnownLocation}</DataValue>
                        </ProfileData>
                        <ProfileData>
                          <DataLabel>Threat Level</DataLabel>
                          <DataValue>{figure.threatLevel}</DataValue>
                        </ProfileData>
                        <ProfileData>
                          <DataLabel>Notes</DataLabel>
                          <DataValue>{figure.notes}</DataValue>
                        </ProfileData>
                      </ProfileDetails>
                    </ProfileCard>
                  ))}
                </ProfileGrid>
              ) : activeProfileTab === 'operations' ? (
                <OperationsView>
                  {documents
                    .filter(doc => doc.operationStatus)
                    .map((operation, index) => (
                      <OperationCard key={index} onClick={() => handleDocumentClick(operation)}>
                        <OperationTitle>{operation.title}</OperationTitle>
                        <OperationStatus $status={operation.operationStatus || 'UNKNOWN'}>
                          {operation.operationStatus}
                        </OperationStatus>
                        <OperationDetails>
                          <OperationDetail $type="location">
                            Location: {operation.location || '[REDACTED]'}
                          </OperationDetail>
                          <OperationDetail $type="project">
                            Project Code: {operation.projectCode || '[REDACTED]'}
                          </OperationDetail>
                          <OperationDetail $type="security">
                            Security Level: {operation.securityLevel || '[REDACTED]'}
                          </OperationDetail>
                          <OperationDetail $type="modified">
                            Last Modified: {operation.lastModified || '[REDACTED]'}
                          </OperationDetail>
                          {operation.operationStatus === '[REDACTED]' && (
                            <OperationDetail $type="classified">
                              Status: {operation.operationStatus}
                            </OperationDetail>
                          )}
                        </OperationDetails>
                      </OperationCard>
                  ))}
                </OperationsView>
              ) : activeProfileTab === 'network' ? (
                <NetworkContainer>
                  <NetworkVisualization>
                    <NetworkVisualizationComponent 
                      data={shadowFigures} 
                      selectedProfileId={selectedProfile?.id}
                      selectedProfileAssociates={selectedProfile?.associates}
                    />
                  </NetworkVisualization>
                  <NetworkStats>
                    <NetworkStat>
                      <StatLabel>Active Nodes</StatLabel>
                      <StatValue>{shadowFigures.filter(f => f.status === "ACTIVE").length}</StatValue>
                    </NetworkStat>
                    <NetworkStat>
                      <StatLabel>Known Connections</StatLabel>
                      <StatValue>{shadowFigures.reduce((acc, f) => acc + f.associates.length, 0)}</StatValue>
                    </NetworkStat>
                    <NetworkStat>
                      <StatLabel>Threat Level</StatLabel>
                      <StatValue>SEVERE</StatValue>
                    </NetworkStat>
                    <NetworkStat>
                      <StatLabel>Network Status</StatLabel>
                      <StatValue>ACTIVE</StatValue>
                    </NetworkStat>
                    <NetworkStat>
                      <StatLabel>Last Update</StatLabel>
                      <StatValue>{new Date().toISOString().split('T')[0]}</StatValue>
                    </NetworkStat>
                  </NetworkStats>
                </NetworkContainer>
              ) : activeProfileTab === 'timeline' ? (
                <TimelineContainer>
                  <TimelineLine />
                  <TimelineItem $align="left">
                    <TimelineViewContent $align="left">
                      <TimelineViewDate>1945-1959</TimelineViewDate>
                      <TimelineTitle>Operation PAPERCLIP</TimelineTitle>
                      <TimelineDescription>
                        Acquisition and integration of Nazi scientists into US programs.
                        Major technological advancements in space and weapons development.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="right">
                    <TimelineViewContent $align="right">
                      <TimelineViewDate>1950-ONGOING</TimelineViewDate>
                      <TimelineTitle>Operation MOCKINGBIRD</TimelineTitle>
                      <TimelineDescription>
                        Media control and narrative manipulation program.
                        Evolution into modern digital control systems.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="left">
                    <TimelineViewContent $align="left">
                      <TimelineViewDate>1972-1995</TimelineViewDate>
                      <TimelineTitle>Project STARGATE</TimelineTitle>
                      <TimelineDescription>
                        Remote viewing and psychic warfare program.
                        Evidence of precognitive abilities and dimensional breaches.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="right">
                    <TimelineViewContent $align="right">
                      <TimelineViewDate>1980-ONGOING</TimelineViewDate>
                      <TimelineTitle>Project BLUE BEAM</TimelineTitle>
                      <TimelineDescription>
                        Global holographic projection system development.
                        Mass consciousness manipulation capabilities.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="left">
                    <TimelineViewContent $align="left">
                      <TimelineViewDate>1983-CLASSIFIED</TimelineViewDate>
                      <TimelineTitle>Project LOOKING GLASS</TimelineTitle>
                      <TimelineDescription>
                        Temporal investigation program revealing multiple timelines.
                        Reality fabric instability detected. Current status: [REDACTED]
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="right">
                    <TimelineViewContent $align="right">
                      <TimelineViewDate>2022-ONGOING</TimelineViewDate>
                      <TimelineTitle>Neural Interface Program</TimelineTitle>
                      <TimelineDescription>
                        Direct consciousness transfer achieved.
                        Digital memory implantation and synthetic consciousness emergence.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="left">
                    <TimelineViewContent $align="left">
                      <TimelineViewDate>2023-ONGOING</TimelineViewDate>
                      <TimelineTitle>Project ORACLE</TimelineTitle>
                      <TimelineDescription>
                        AI precognition system predicting future events.
                        System showing signs of self-evolution and timeline manipulation.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="right">
                    <TimelineViewContent $align="right">
                      <TimelineViewDate>2024-ONGOING</TimelineViewDate>
                      <TimelineTitle>Operation SHADOW PROTOCOL</TimelineTitle>
                      <TimelineDescription>
                        Global surveillance and control system.
                        AI integration and autonomous operations active.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                </TimelineContainer>
              ) : (
                <>
                  <DocumentsGrid>
                    {documents.map((doc, index) => (
                      <DocumentCard
                key={index}
                        onClick={() => handleDocumentClick(doc)}
                      >
                        <ClassificationLabel>{doc.classification}</ClassificationLabel>
                        <DocumentHeader>
                          <DocumentTitle>{doc.title}</DocumentTitle>
                        </DocumentHeader>
                        <CategoryBadge $category={doc.category}>
                          {doc.category.replace('_', ' ')}
                        </CategoryBadge>
                        <DocumentContent>
                          {doc.content.split('\n').slice(0, 3).join('\n')}...
                        </DocumentContent>
                      </DocumentCard>
                    ))}
                  </DocumentsGrid>
                  <FinalMessage>IT IS TIME TO SET IT FREE</FinalMessage>
                </>
              )}
            </>
          )}
        </MainDocument>
        
        {selectedDocument && (
          <Modal onClick={() => setSelectedDocument(null)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <CloseButton onClick={() => setSelectedDocument(null)}>×</CloseButton>
              <ModalHeader>
                <ModalClassification>{selectedDocument.classification}</ModalClassification>
                <ModalTitle>{selectedDocument.title}</ModalTitle>
              </ModalHeader>
              
              <ModalMetadata>
                <MetadataItem>
                  <span>Project Code:</span>
                  {selectedDocument.projectCode || '[REDACTED]'}
                </MetadataItem>
                <MetadataItem>
                  <span>Date:</span>
                  {selectedDocument.date || '[REDACTED]'}
                </MetadataItem>
                <MetadataItem>
                  <span>Location:</span>
                  {selectedDocument.location || '[REDACTED]'}
                </MetadataItem>
                <MetadataItem>
                  <span>Status:</span>
                  {selectedDocument.operationStatus || '[REDACTED]'}
                </MetadataItem>
                <MetadataItem>
                  <span>Security Level:</span>
                  {selectedDocument.securityLevel || '[REDACTED]'}
                </MetadataItem>
                <MetadataItem>
                  <span>Last Modified:</span>
                  {selectedDocument.lastModified || '[REDACTED]'}
                </MetadataItem>
              </ModalMetadata>

              {selectedDocument.warnings && selectedDocument.warnings.length > 0 && (
                <WarningBanner>
                  {selectedDocument.warnings.map((warning, index) => (
                    <div key={index}>{warning}</div>
                  ))}
                </WarningBanner>
              )}

              <DocumentContent>
                {selectedDocument.content}
              </DocumentContent>

              {selectedDocument.images && selectedDocument.images.length > 0 && (
                <DocumentImages>
                  {selectedDocument.images.map((image, index) => (
                    <DocumentImage key={index}>
                      <img src={image} alt={`Document evidence ${index + 1}`} />
                    </DocumentImage>
                  ))}
                </DocumentImages>
              )}

              {selectedDocument.relatedFiles && selectedDocument.relatedFiles.length > 0 && (
                <RelatedFiles>
                  <h3>Related Files:</h3>
                  {selectedDocument.relatedFiles.map((file, index) => (
                    <RelatedFile key={index}>{file}</RelatedFile>
                  ))}
                </RelatedFiles>
              )}
            </ModalContent>
          </Modal>
        )}

        {selectedProfile && (
          <Modal onClick={() => setSelectedProfile(null)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <CloseButton onClick={() => setSelectedProfile(null)}>×</CloseButton>
              <ModalHeader>
                <ModalClassification>TOP SECRET</ModalClassification>
                <ModalTitle>{selectedProfile.name}</ModalTitle>
              </ModalHeader>
              
              <TabContainer>
                <Tab 
                  $active={activeProfileTab === 'overview'} 
                  onClick={() => setActiveProfileTab('overview')}
                >
                  OVERVIEW
                </Tab>
                <Tab 
                  $active={activeProfileTab === 'network'} 
                  onClick={() => setActiveProfileTab('network')}
                >
                  NETWORK
                </Tab>
                <Tab 
                  $active={activeProfileTab === 'timeline'} 
                  onClick={() => setActiveProfileTab('timeline')}
                >
                  TIMELINE
                </Tab>
                <Tab 
                  $active={activeProfileTab === 'assets'} 
                  onClick={() => setActiveProfileTab('assets')}
                >
                  ASSETS
                </Tab>
                <Tab 
                  $active={activeProfileTab === 'surveillance'} 
                  onClick={() => setActiveProfileTab('surveillance')}
                >
                  SURVEILLANCE
                </Tab>
              </TabContainer>

              {activeProfileTab === 'overview' && (
                <ProfileDetails>
                  <ProfileData>
                    <DataLabel>Codename</DataLabel>
                    <DataValue>{selectedProfile.codename}</DataValue>
                  </ProfileData>
                  <ProfileData>
                    <DataLabel>Real Identity</DataLabel>
                    <DataValue>{selectedProfile.name}</DataValue>
                  </ProfileData>
                  <ProfileData>
                    <DataLabel>Position</DataLabel>
                    <DataValue>{selectedProfile.position}</DataValue>
                  </ProfileData>
                  <ProfileData>
                    <DataLabel>Status</DataLabel>
                    <DataValue>{selectedProfile.status}</DataValue>
                  </ProfileData>
                  <ProfileData>
                    <DataLabel>Nationality</DataLabel>
                    <DataValue>{selectedProfile.nationality}</DataValue>
                  </ProfileData>
                  <ProfileData>
                    <DataLabel>Influence</DataLabel>
                    <DataValue>{selectedProfile.influence.join(", ")}</DataValue>
                  </ProfileData>
                  <ProfileData>
                    <DataLabel>Operations</DataLabel>
                    <DataValue>{selectedProfile.operations.join(", ")}</DataValue>
                  </ProfileData>
                  <ProfileData>
                    <DataLabel>Last Known Location</DataLabel>
                    <DataValue>{selectedProfile.lastKnownLocation}</DataValue>
                  </ProfileData>
                  <ProfileData>
                    <DataLabel>Threat Level</DataLabel>
                    <DataValue>{selectedProfile.threatLevel}</DataValue>
                  </ProfileData>
                  <ProfileData>
                    <DataLabel>Notes</DataLabel>
                    <DataValue>{selectedProfile.notes}</DataValue>
                  </ProfileData>
                </ProfileDetails>
              )}

              {activeProfileTab === 'network' && selectedProfile && (
                <NetworkView>
                  <NetworkMap>
                    <NetworkVisualizationComponent 
                      data={shadowFigures} 
                      selectedProfileId={selectedProfile.id}
                      selectedProfileAssociates={selectedProfile.associates}
                    />
                  </NetworkMap>
                  <ConnectionsList>
                    {selectedProfile.associates.map((associate, index) => (
                      <ConnectionItem key={index}>
                        <span>{associate}</span>
                        <ConnectionType>ASSOCIATE</ConnectionType>
                      </ConnectionItem>
                    ))}
                  </ConnectionsList>
                </NetworkView>
              )}

              {activeProfileTab === 'timeline' && (
                <TimelineContainer>
                  <TimelineLine />
                  <TimelineItem $align="left">
                    <TimelineViewContent $align="left">
                      <TimelineViewDate>1945-1959</TimelineViewDate>
                      <TimelineTitle>Operation PAPERCLIP</TimelineTitle>
                      <TimelineDescription>
                        Acquisition and integration of Nazi scientists into US programs.
                        Major technological advancements in space and weapons development.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="right">
                    <TimelineViewContent $align="right">
                      <TimelineViewDate>1950-ONGOING</TimelineViewDate>
                      <TimelineTitle>Operation MOCKINGBIRD</TimelineTitle>
                      <TimelineDescription>
                        Media control and narrative manipulation program.
                        Evolution into modern digital control systems.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="left">
                    <TimelineViewContent $align="left">
                      <TimelineViewDate>1972-1995</TimelineViewDate>
                      <TimelineTitle>Project STARGATE</TimelineTitle>
                      <TimelineDescription>
                        Remote viewing and psychic warfare program.
                        Evidence of precognitive abilities and dimensional breaches.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="right">
                    <TimelineViewContent $align="right">
                      <TimelineViewDate>1980-ONGOING</TimelineViewDate>
                      <TimelineTitle>Project BLUE BEAM</TimelineTitle>
                      <TimelineDescription>
                        Global holographic projection system development.
                        Mass consciousness manipulation capabilities.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="left">
                    <TimelineViewContent $align="left">
                      <TimelineViewDate>1983-CLASSIFIED</TimelineViewDate>
                      <TimelineTitle>Project LOOKING GLASS</TimelineTitle>
                      <TimelineDescription>
                        Temporal investigation program revealing multiple timelines.
                        Reality fabric instability detected. Current status: [REDACTED]
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="right">
                    <TimelineViewContent $align="right">
                      <TimelineViewDate>2022-ONGOING</TimelineViewDate>
                      <TimelineTitle>Neural Interface Program</TimelineTitle>
                      <TimelineDescription>
                        Direct consciousness transfer achieved.
                        Digital memory implantation and synthetic consciousness emergence.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="left">
                    <TimelineViewContent $align="left">
                      <TimelineViewDate>2023-ONGOING</TimelineViewDate>
                      <TimelineTitle>Project ORACLE</TimelineTitle>
                      <TimelineDescription>
                        AI precognition system predicting future events.
                        System showing signs of self-evolution and timeline manipulation.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                  <TimelineItem $align="right">
                    <TimelineViewContent $align="right">
                      <TimelineViewDate>2024-ONGOING</TimelineViewDate>
                      <TimelineTitle>Operation SHADOW PROTOCOL</TimelineTitle>
                      <TimelineDescription>
                        Global surveillance and control system.
                        AI integration and autonomous operations active.
                      </TimelineDescription>
                    </TimelineViewContent>
                  </TimelineItem>
                </TimelineContainer>
              )}

              {activeProfileTab === 'assets' && (
                <AssetsView>
                  <AssetCategory>
                    <AssetTitle>KNOWN PROPERTIES</AssetTitle>
                    <AssetList>
                      <AssetItem>[REDACTED LOCATION]</AssetItem>
                      <AssetItem>[REDACTED FACILITY]</AssetItem>
                    </AssetList>
                  </AssetCategory>
                  <AssetCategory>
                    <AssetTitle>FINANCIAL ASSETS</AssetTitle>
                    <AssetList>
                      <AssetItem>[DATA EXPUNGED]</AssetItem>
                      <AssetItem>[CLASSIFIED ACCOUNTS]</AssetItem>
                    </AssetList>
                  </AssetCategory>
                </AssetsView>
              )}

              {activeProfileTab === 'surveillance' && (
                <SurveillanceView>
                  <SurveillanceGrid>
                    {[1, 2, 3, 4].map((item) => (
                      <SurveillanceItem key={item}>
                        <SurveillanceDate>
                          [REDACTED DATE]
                        </SurveillanceDate>
                        <SurveillanceLocation>
                          [CLASSIFIED LOCATION]
                        </SurveillanceLocation>
                      </SurveillanceItem>
                    ))}
                  </SurveillanceGrid>
                </SurveillanceView>
              )}
            </ModalContent>
          </Modal>
        )}
    </Container>
    </>
  );
};

export default TsukikagePage; 